import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	ElementRef,
	Input,
	input,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { AsyncSubject, forkJoin, of, takeUntil } from 'rxjs';
import { ExternalAccountModel } from 'src/lib/services/api/external-accounts/external-account.model';
import { PermissionUserRolesModel } from 'src/lib/services/api/permission-manager/permission-user-roles.model';
import { UserUpdateRequestArgument } from 'src/lib/services/api/users/update-request/users-update-request.argument';
import { UsersUpdateRequestsService } from 'src/lib/services/api/users/update-request/users-update-requests.service';
import {
	UserDataGAModel,
	UserDataProfileModel,
	UserDataProgramEnrollmentModel,
	UserDataUserModel,
} from 'src/lib/services/api/users/user-data.model';
import { UsersService } from 'src/lib/services/api/users/users.service';
import { GroupValidationModule } from 'src/lib/templates/layout/group-validation/group-validation.module';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { StringEnumOption } from 'src/lib/utilities/api/patterns/dynamic-fields/string-enum-option.model';
import { isArray } from 'src/lib/utilities/compare';
import { ExternalAccountCreationModalService } from 'src/lib/views/modals/external-account-creation-modal/external-account-creation-modal.service';
import { RoleEditModalService } from 'src/lib/views/modals/role-edit-modal/role-edit-modal.service';
import { SpinWhileDirective } from '../../../../../templates/layout/spin-while/spin-while.directive';

export interface AccountFormModel {
	title: string;
	admin_type: string[];
	employee_id: string;
}

@Component({
	selector: 'ae-profile-account-edit',
	templateUrl: './profile-account-edit.component.html',
	styleUrls: ['./profile-account-edit.component.scss'],
	imports: [
		DatePipe,
		FormsModule,
		GroupValidationModule,
		NgSelectModule,
		ReactiveFormsModule,
		RouterLink,
		SpinWhileDirective,
	],
})
export class ProfileAccountEditComponent implements OnInit {
	private _unsubscribe$ = new AsyncSubject<null>();
	private initialAccountFormValue: AccountFormModel;

	@Input() public accountData: UserDataUserModel;
	@Input() public loading: boolean;
	@Input() public enableEdit: () => void;
	@Input() public existingPendingChange: boolean;
	public readonly gaData = input<UserDataGAModel>();
	@Input() public accountSaveListener: (forceRefresh: boolean) => void;
	@Input() public currentUserId: number;
	@Input() public profileData: UserDataProfileModel;
	@Input() public formattedAccessType: string[];
	@Input() public assignedStudents: { name: string; id: number }[];
	@Input() public canEditId: boolean;
	@Input() public canEditRoles: boolean;
	@Input() public canEditLinkedAccounts: boolean;
	@Input() public canEditPrimaryProgram: boolean = false;
	@Input() public externalAccounts: ExternalAccountModel[];

	@ViewChild('rootElement', { static: true })
	rootElement: ElementRef<HTMLElement>;

	public dropdownAdminType: StringEnumOption[] = [
		{ key: 'counselor', title: 'Counselor' },
		{ key: 'school_administrator', title: 'Administrator' },
		{ key: 'assistant', title: 'Assistant' },
	];
	public savingInfo: boolean = false;
	public pendingPrimaryProgramId: number | null;

	public accountFormGroup: FormGroup<FormControlWrapper<AccountFormModel>>;

	constructor(
		private fb: FormBuilder,
		private userRequestService: UsersUpdateRequestsService,
		private usersService: UsersService,
		private toastr: ToastrService,
		private roleEditModalService: RoleEditModalService,
		private externalAccountCreationModalService: ExternalAccountCreationModalService,
	) {}

	ngOnInit() {
		this.pendingPrimaryProgramId = this.gaData()?.primary_program_id ?? null;
		this.accountFormGroup = this.fb.group<FormControlWrapper<AccountFormModel>>(
			{
				admin_type: new FormControl(
					this.accountData?.admin_type ? this.accountData.admin_type : null,
				),
				title: new FormControl(this.profileData?.profile_user_title),
				employee_id: new FormControl(this.profileData?.profile_employee_id),
			},
		);
		const initialAccountFormValue = this.accountFormGroup.getRawValue();
		this.initialAccountFormValue = {
			...initialAccountFormValue,
			admin_type: [...(initialAccountFormValue.admin_type ?? [])],
		};
	}

	public save = () => {
		this.savingInfo = true;
		const formCtrls = this.accountFormGroup.controls;
		const profileContactArgs: UserUpdateRequestArgument = {
			user: {
				admin_type: formCtrls.admin_type.value,
			},
			profile: {
				profile_user_title: formCtrls.title.value,
				profile_employee_id: formCtrls.employee_id.value,
			},
		};
		this.accountFormGroup.disable();
		forkJoin({
			account: this.hasAccountChanges
				? this.userRequestService.createUpdateRequest(
						this.currentUserId,
						profileContactArgs,
					)
				: of(null),
			primaryProgram: this.shouldSavePrimaryProgram
				? this.usersService.setUserData(this.currentUserId, {
						ga: { primary_program_id: this.pendingPrimaryProgramId },
					})
				: of(null),
		}).subscribe({
			next: ({ account: response }) => {
				this.accountFormGroup.enable();
				this.savingInfo = false;
				if (response == null || response.success) {
					if (
						response?.update_request?.updates &&
						!isArray(response.update_request?.updates)
					) {
						this.toastr.success(
							'Account info update has been requested. Your changes will not be shown until they are approved.',
						);
					} else {
						this.toastr.success('Account info has been updated.');
					}
					this.accountSaveListener(true);
				} else {
					this.toastr.error(
						`There was an error when updating account info ${(
							response as unknown as { error_messages: string[] }
						)?.error_messages.join('\n')}`,
					);
				}
			},
			error: () => {
				this.accountFormGroup.enable();
				this.savingInfo = false;
				this.toastr.error(`There was an error when updating account info`);
			},
		});
	};

	public get hasAccountChanges(): boolean {
		if (this.accountFormGroup == null || this.initialAccountFormValue == null) {
			return false;
		}

		const current = this.accountFormGroup.getRawValue();
		return (
			current.title !== this.initialAccountFormValue.title ||
			current.employee_id !== this.initialAccountFormValue.employee_id ||
			JSON.stringify([...(current.admin_type ?? [])].sort()) !==
				JSON.stringify(
					[...(this.initialAccountFormValue.admin_type ?? [])].sort(),
				)
		);
	}

	public get hasChanges(): boolean {
		return this.hasAccountChanges || this.shouldSavePrimaryProgram;
	}

	public readonly programEnrollments = computed(() =>
		[...(this.gaData()?.program_enrollments ?? [])].sort((a, b) =>
			a.title.localeCompare(b.title),
		),
	);

	public get shouldSavePrimaryProgram(): boolean {
		return (
			this.canEditPrimaryProgram &&
			this.pendingPrimaryProgramId !== this.gaData()?.primary_program_id
		);
	}

	public isPrimaryProgram = (
		enrollment: UserDataProgramEnrollmentModel,
	): boolean => enrollment.program_id === this.pendingPrimaryProgramId;

	public canSelectPrimaryProgram = (
		enrollment: UserDataProgramEnrollmentModel,
	): boolean =>
		this.canEditPrimaryProgram &&
		enrollment.is_active &&
		enrollment.is_administrator;

	public selectPrimaryProgram = (
		enrollment: UserDataProgramEnrollmentModel,
	): void => {
		if (this.canSelectPrimaryProgram(enrollment)) {
			this.pendingPrimaryProgramId = enrollment.program_id;
		}
	};

	public editRoles = () => {
		const value = new PermissionUserRolesModel();
		value.name = `${this.profileData.profile_first_name} ${this.profileData.profile_last_name}`;
		value.roles = this.accountData.roles;
		value.uid = this.currentUserId;
		value.institute_roles = this.accountData.institute_roles;
		this.roleEditModalService
			.openModal$(value)
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((r) => {
				if (r) {
					this.accountSaveListener(true);
				}
			});
	};

	public addExternalAccounts = () => {
		this.externalAccountCreationModalService
			.openModal$(this.currentUserId)
			.subscribe((r) => {
				if (r) {
					this.accountSaveListener(true);
				}
			});
	};
}
